// src/pages/dataSells/DataSellsPage.jsx
import React, { useState, useEffect } from 'react';
import { FaChartLine, FaShoppingCart, FaSync } from 'react-icons/fa';
import useDataSells from '../../hooks/useDataSells';
import useAuthStore from '../../store/authStore';
import { get } from '../../helpers/apiHelper';
import FileUploadSection from '../../components/dataSells/FileUploadSection';
import UploadedFilesList from '../../components/dataSells/UploadedFilesList';
import SalesTable from '../../components/dataSells/SalesTable';
import NotificationCenter from '../../components/dataSells/NotificationCenter';

/**
 * Página principal de Gestión de Ventas
 */
const DataSellsPage = () => {
    const { user } = useAuthStore();
    const {
        loading,
        ventas,
        archivosSubidos,
        notifications,
        subirArchivo,
        validarArchivoSubido,
        mapearConIA,
        procesarArchivoVentas,
        cargarVentas,
        cargarArchivosSubidos,
        eliminarArchivoSubido,
        removeNotification,
        clearNotifications
    } = useDataSells();

    const [marcas, setMarcas] = useState([]);
    const [tiendas, setTiendas] = useState([]);
    const [selectedTab, setSelectedTab] = useState('upload'); // 'upload', 'files', 'sales'
    const [estadisticas, setEstadisticas] = useState({
        totalVentas: 0,
        totalMonto: 0,
        ventasHoy: 0
    });
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
    const [showVentaModal, setShowVentaModal] = useState(false);

    useEffect(() => {
        cargarDatosIniciales();
    }, [user]);

    useEffect(() => {
        // Calcular estadísticas
        if (ventas.length > 0) {
            const totalMonto = ventas.reduce((sum, venta) => sum + (venta.monto || 0), 0);
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            const ventasHoy = ventas.filter(venta => {
                const fechaVenta = new Date(venta.fecha);
                fechaVenta.setHours(0, 0, 0, 0);
                return fechaVenta.getTime() === hoy.getTime();
            }).length;

            setEstadisticas({
                totalVentas: ventas.length,
                totalMonto,
                ventasHoy
            });
        }
    }, [ventas]);

    const cargarDatosIniciales = async () => {
        try {
            // Cargar marcas
            let marcasData = [];
            try {
                if (user?.userId) {
                    marcasData = await get(`marcas/user/${user.userId}`);
                } else {
                    marcasData = await get('marcas');
                }
                setMarcas(Array.isArray(marcasData) ? marcasData : []);
            } catch (error) {
                console.error('Error cargando marcas:', error);
                setMarcas([]);
            }

            // Cargar todas las tiendas
            try {
                const tiendasData = await get('tiendas');
                setTiendas(Array.isArray(tiendasData) ? tiendasData : []);
            } catch (error) {
                console.error('Error cargando tiendas:', error);
                setTiendas([]);
            }

            // Cargar archivos subidos
            try {
                await cargarArchivosSubidos();
            } catch (error) {
                console.error('Error cargando archivos subidos:', error);
                // No es crítico, continuar sin archivos
            }

            // Cargar ventas
            try {
                await cargarVentas();
            } catch (error) {
                console.error('Error cargando ventas:', error);
                // No es crítico, continuar sin ventas
            }
        } catch (error) {
            console.error('Error en carga inicial:', error);
        }
    };

    const handleUpload = async (file, marcaId, tiendasIds) => {
        try {
            await subirArchivo(file, marcaId, tiendasIds);
            // El archivo ya se añade automáticamente al estado
        } catch (error) {
            console.error('Error subiendo archivo:', error);
        }
    };

    const handleEliminar = async (archivoId) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este archivo? Esta acción no se puede deshacer.')) {
            try {
                await eliminarArchivoSubido(archivoId);
            } catch (error) {
                console.error('Error eliminando archivo:', error);
            }
        }
    };

    const handleValidar = async (archivoId) => {
        try {
            await validarArchivoSubido(archivoId);
        } catch (error) {
            console.error('Error validando archivo:', error);
        }
    };

    const handleMapearIA = async (archivoId) => {
        try {
            await mapearConIA(archivoId);
        } catch (error) {
            console.error('Error mapeando con IA:', error);
        }
    };

    const handleProcesar = async (archivoId) => {
        try {
            await procesarArchivoVentas(archivoId);
            // Recargar ventas
            await cargarVentas();
        } catch (error) {
            console.error('Error procesando archivo:', error);
        }
    };

    const handleVerDetalleVenta = (venta) => {
        setVentaSeleccionada(venta);
        setShowVentaModal(true);
    };

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        <FaShoppingCart className="mr-3 text-blue-500" />
                        Gestión de Ventas
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Importa y gestiona tus ventas desde archivos CSV o Excel
                    </p>
                </div>

                {/* Estadísticas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Ventas</p>
                                <p className="text-2xl font-bold text-gray-900">{estadisticas.totalVentas}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full">
                                <FaShoppingCart className="text-2xl text-blue-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Monto Total</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(estadisticas.totalMonto)}
                                </p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full">
                                <FaChartLine className="text-2xl text-green-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Ventas Hoy</p>
                                <p className="text-2xl font-bold text-gray-900">{estadisticas.ventasHoy}</p>
                            </div>
                            <div className="p-3 bg-purple-100 rounded-full">
                                <FaSync className="text-2xl text-purple-500" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setSelectedTab('upload')}
                                className={`${selectedTab === 'upload'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                            >
                                Subir Archivo
                            </button>
                            <button
                                onClick={() => setSelectedTab('files')}
                                className={`${selectedTab === 'files'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                            >
                                Archivos Subidos
                                {archivosSubidos.length > 0 && (
                                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                                        {archivosSubidos.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setSelectedTab('sales')}
                                className={`${selectedTab === 'sales'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                            >
                                Ver Ventas
                                {ventas.length > 0 && (
                                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                                        {ventas.length}
                                    </span>
                                )}
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {selectedTab === 'upload' && (
                            <FileUploadSection
                                marcas={marcas}
                                tiendas={tiendas}
                                onUpload={handleUpload}
                                loading={loading}
                            />
                        )}

                        {selectedTab === 'files' && (
                            <UploadedFilesList
                                archivos={archivosSubidos}
                                ventas={ventas}
                                onValidar={handleValidar}
                                onMapearIA={handleMapearIA}
                                onProcesar={handleProcesar}
                                onEliminar={handleEliminar}
                                loading={loading}
                            />
                        )}

                        {selectedTab === 'sales' && (
                            <SalesTable
                                ventas={ventas}
                                marcas={marcas}
                                tiendas={tiendas}
                                onVerDetalle={handleVerDetalleVenta}
                                loading={loading}
                            />
                        )}
                    </div>

                    {/* Sidebar - Notificaciones */}
                    <div className="lg:col-span-1">
                        <NotificationCenter
                            notifications={notifications}
                            onRemoveNotification={removeNotification}
                            onClearAll={clearNotifications}
                        />
                    </div>
                </div>
            </div>

            {/* Modal de Detalle de Venta */}
            {showVentaModal && ventaSeleccionada && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-xl font-semibold">
                                Detalle de Venta: {ventaSeleccionada.idVenta}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowVentaModal(false);
                                    setVentaSeleccionada(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Fecha</p>
                                    <p className="font-medium">{new Date(ventaSeleccionada.fecha).toLocaleString('es-ES')}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Monto</p>
                                    <p className="font-medium text-lg">{formatCurrency(ventaSeleccionada.monto, ventaSeleccionada.moneda)}</p>
                                </div>
                                {ventaSeleccionada.informacionCliente && (
                                    <>
                                        <div>
                                            <p className="text-sm text-gray-600">Cliente</p>
                                            <p className="font-medium">{ventaSeleccionada.informacionCliente.nombre || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Email</p>
                                            <p className="font-medium">{ventaSeleccionada.informacionCliente.email || 'N/A'}</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            {ventaSeleccionada.productos && ventaSeleccionada.productos.length > 0 && (
                                <div>
                                    <h3 className="font-semibold mb-2">Productos</h3>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cantidad</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Precio</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {ventaSeleccionada.productos.map((producto, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-2 text-sm">{producto.nombre}</td>
                                                        <td className="px-4 py-2 text-sm">{producto.cantidad || 1}</td>
                                                        <td className="px-4 py-2 text-sm">{formatCurrency(producto.precio)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {ventaSeleccionada.datosOriginales && (
                                <div>
                                    <h3 className="font-semibold mb-2">Datos Originales</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                                        <pre className="text-xs text-gray-600">
                                            {JSON.stringify(ventaSeleccionada.datosOriginales, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataSellsPage;

